import React from 'react';
import { Link } from 'react-router-dom';
import Button from './ui/Button';
import { getImageUrl as getImageUrlUtil } from '../utils/imageUtils';

const ArtisanCard = ({ artisan }) => {
  const getImageUrl = (photo) => {
    if (!photo) return '/images/placeholder-artisan.svg';
    return getImageUrlUtil(photo) || '/images/placeholder-artisan.svg';
  };

  // Fonction pour détecter si la description est un lien
  const isUrl = (text) => {
    if (!text) return false;
    const urlRegex = /^https?:\/\/[^\s]+$/;
    return urlRegex.test(text.trim());
  };

  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const artisanUrl = `${window.location.origin}/artisans/${artisan._id}`;
    const message = `Bonjour ${artisan.nom}, j'aimerais en savoir plus sur vos produits artisanaux.\n\nVotre profil: ${artisanUrl}`;
    const whatsappUrl = `https://wa.me/${artisan.whatsapp?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handlePhoneClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = `tel:${artisan.telephone}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-transparent hover:border-accent-400 dark:hover:border-accent-500 overflow-hidden group relative">
      {/* Motif décoratif subtil au hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <div 
          className="absolute inset-0" 
          style={{ 
            backgroundImage: 'url(/images/patterns.jpeg)',
            backgroundSize: '150px 150px',
            opacity: 0.03
          }}
        />
      </div>
      
      <Link to={`/artisans/${artisan._id}`} className="block relative z-10">
        <div className="relative h-32 sm:h-36 overflow-hidden bg-gradient-to-br from-primary-100 to-primary-200">
          <img
            src={getImageUrl(artisan.photoProfil)}
            alt={artisan.nom}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              e.target.src = '/images/placeholder-artisan.svg';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
            <div className="flex space-x-2 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
              <Button variant="icon" size="sm" onClick={handleWhatsAppClick} ariaLabel={`Contacter ${artisan.nom} via WhatsApp`} className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white">
                <i className="fab fa-whatsapp text-sm" aria-hidden="true"></i>
              </Button>
              <Button variant="icon" size="sm" onClick={handlePhoneClick} ariaLabel={`Appeler ${artisan.nom}`} className="w-8 h-8 bg-blue-500 hover:bg-blue-600 text-white">
                <i className="fas fa-phone text-sm" aria-hidden="true"></i>
              </Button>
            </div>
          </div>
        </div>

        <div className="p-2 sm:p-3">
          <h3 className="font-display text-sm sm:text-base font-bold text-primary-900 dark:text-primary-300 mb-0.5 hover:text-accent-600 dark:hover:text-accent-400 transition-colors line-clamp-1">{artisan.nom}</h3>
          <p className="text-accent-600 dark:text-accent-400 font-medium mb-1 text-xs sm:text-sm line-clamp-1">{artisan.specialite}</p>
          
          <div className="flex items-center text-gray-600 dark:text-gray-400 mb-1 sm:mb-2">
            <i className="fas fa-map-marker-alt text-primary-600 dark:text-primary-400 mr-1 text-xs"></i>
            <span className="text-xs font-medium">{artisan.region}</span>
          </div>

          {artisan.description && (
            <div className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed mb-1 sm:mb-2 line-clamp-1 hidden sm:block">
              {isUrl(artisan.description) ? (
                <a 
                  href={artisan.description.trim()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-primary-600 dark:text-primary-400 hover:text-accent-600 dark:hover:text-accent-400 underline break-all"
                >
                  {artisan.description.length > 60
                    ? `${artisan.description.substring(0, 60)}...`
                    : artisan.description}
                </a>
              ) : (
                <span>
                  {artisan.description.length > 60
                    ? `${artisan.description.substring(0, 60)}...`
                    : artisan.description}
                </span>
              )}
            </div>
          )}

          {artisan.estVerifie && (
            <div className="inline-flex items-center bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-1.5 py-0.5 rounded-full text-xs font-medium mb-1">
              <i className="fas fa-check-circle mr-1 text-xs"></i>
              <span className="hidden sm:inline">Vérifié</span>
              <i className="fas fa-check sm:hidden"></i>
            </div>
          )}
        </div>
      </Link>

      <div className="px-2 pb-2 sm:px-3 sm:pb-3">
        <div className="text-center p-2 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded border border-primary-200 dark:border-primary-700">
          <div className="flex items-center justify-center gap-1.5">
            <i className="fas fa-box text-primary-600 dark:text-primary-400 text-sm"></i>
            <span className="text-sm font-bold text-primary-800 dark:text-primary-300">
              {artisan.nombreProduits || 0} {artisan.nombreProduits > 1 ? 'produits' : 'produit'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtisanCard;